import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import { useRefs } from '@halo/hooks';
import { ResizablePanelsContext } from './context';
import { calculateNewWidth, validateWidthConfig } from './utils';

export interface ResizablePanelsRootProps extends Omit<BoxProps, 'onResize'> {
  defaultWidths?: number[];
  minWidths?: number[];
  maxWidths?: number[];
  onResize?: (widths: number[]) => void;
  onResizeEnd?: (widths: number[]) => void;
}

export const ResizablePanelsRoot: React.FC<ResizablePanelsRootProps> = ({
  children,
  defaultWidths = [200, 387],
  minWidths = [150, 200],
  maxWidths,
  onResize,
  onResizeEnd,
  className,
  ...boxProps
}) => {
  const [panelWidths, setPanelWidths] = useState<number[]>(defaultWidths);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandleIndex, setDragHandleIndex] = useState<number>(-1);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartWidths, setDragStartWidths] = useState<number[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    validateWidthConfig(defaultWidths, minWidths, maxWidths);
  }, [defaultWidths, minWidths, maxWidths]);

  useEffect(() => {
    setPanelWidths(defaultWidths);
  }, [defaultWidths]);

  const handleResizeStart = useCallback(
    (handleIndex: number, startX: number) => {
      setIsDragging(true);
      setDragHandleIndex(handleIndex);
      setDragStartX(startX);
      setDragStartWidths([...panelWidths]);

      // console.log('start dragging:', {
      //   handleIndex,
      //   startX,
      //   currentWidths: panelWidths,
      //   minWidths,
      //   maxWidths,
      // });
    },
    [panelWidths]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || dragHandleIndex === -1) return;

      const deltaX = e.clientX - dragStartX;
      const newWidths = [...dragStartWidths];

      if (dragHandleIndex < newWidths.length) {
        const originalWidth = dragStartWidths[dragHandleIndex];
        const constraints = {
          minWidth: minWidths[dragHandleIndex] ?? 100,
          maxWidth: maxWidths?.[dragHandleIndex],
        };

        const newWidth = calculateNewWidth(originalWidth, deltaX, constraints);
        newWidths[dragHandleIndex] = newWidth;

        // console.log('dragging:', {
        //   handleIndex: dragHandleIndex,
        //   deltaX,
        //   originalWidth,
        //   newWidth,
        //   constraints,
        // });

        setPanelWidths(newWidths);
        onResize?.(newWidths);
      }
    },
    [isDragging, dragHandleIndex, dragStartX, dragStartWidths, minWidths, maxWidths, onResize]
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // console.log('dragging end:', panelWidths);
      setIsDragging(false);
      setDragHandleIndex(-1);
      onResizeEnd?.(panelWidths);
    }
  }, [isDragging, panelWidths, onResizeEnd]);

  const refs = useRefs({
    handleMouseMove,
    handleMouseUp,
  });

  useEffect(() => {
    if (isDragging) {
      const { handleMouseMove, handleMouseUp } = refs.current;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, refs]);

  const contextValue = useMemo(() => {
    return {
      panelWidths,
      isDragging,
      dragHandleIndex,
      minWidths,
      maxWidths,
      onResizeStart: handleResizeStart,
    };
  }, [panelWidths, isDragging, dragHandleIndex, minWidths, maxWidths, handleResizeStart]);

  return (
    <ResizablePanelsContext.Provider value={contextValue}>
      <Box
        ref={containerRef}
        display="flex"
        flexDirection="row"
        height="100%"
        width="100%"
        userSelect={isDragging ? 'none' : 'auto'}
        cursor={isDragging ? 'col-resize' : undefined}
        className={`ResizablePanelsRoot ${className ?? ''}`}
        {...boxProps}
      >
        {children}
      </Box>
    </ResizablePanelsContext.Provider>
  );
};
